import { authentication, createDirectus, realtime, rest } from "@directus/sdk";
import { joinURL } from "ufo";
import type { Schema } from "~~/types";
import { defineNuxtPlugin, useRoute, useRuntimeConfig } from "#imports";

export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute();
  const config = useRuntimeConfig();

  const siteUrl = config.public.SITE_URL; // http://localhost:3000 from your .env
  const directusProxyUrl = joinURL(siteUrl, "/api/proxy");
  const directusUrl = process.server ? config.public.API_URL : directusProxyUrl;

  let customFetch = $fetch;

  // If running on the server, create a custom fetch instance that includes
  // cookies from the original request when calling Directus.
  //
  // Server-side, `directusUrl` is the direct API_URL (not the proxy — see
  // below), so the SDK's requests always target that host. Forwarding the
  // incoming request's own cookies there is safe (same trusted destination
  // the proxy would otherwise call) and is what makes SSR-side auth checks
  // (`performSdkLightweightUserCheck` etc.) see the logged-in session instead
  // of failing with 401 and causing a client/server hydration mismatch.
  if (process.server && nuxtApp.ssrContext && nuxtApp.ssrContext.event) {
    const event = nuxtApp.ssrContext.event;
    customFetch = $fetch.create({
      onRequest({ request, options }) {
        if (typeof request === "string" && request.startsWith(directusUrl)) {
          const originalCookies = event.node.req.headers.cookie;
          if (originalCookies) {
            options.headers = new Headers(options.headers); // Ensure options.headers is a Headers object
            options.headers.set("cookie", originalCookies);
          }
        }
      },
    });
  }

  let directus = createDirectus<Schema>(directusUrl, {
    globals: { fetch: customFetch },
  })
    .with(authentication("session"))
    .with(rest());

  if (process.client) {
    const realtimeUrl = new URL(joinURL(config.public.API_URL, "websocket"));
    realtimeUrl.protocol = realtimeUrl.protocol === "https:" ? "wss:" : "ws:";

    directus = directus.with(
      realtime({
        url: realtimeUrl.toString(),
        authMode: "strict",
        heartbeat: true,
        reconnect: { delay: 2000, retries: 10 },
      }),
    );
  }

  // ** Live Preview Bits **
  // Check if we are in preview mode
  const preview = route.query.preview && route.query.preview === "true";
  const token = route.query.token as string | undefined;

  // If we are in preview mode, we need to use the token from the query string
  if (preview && token) {
    directus.setToken(token);

    nuxtApp.hook("page:finish", () => {
      refreshNuxtData();
    });
  }

  return {
    provide: {
      directus,
    },
  };
});
